import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { ASSETS } from "@/lib/assets";
import { scrollToId } from "@/lib/scroll";
import { Magnetic } from "./Magnetic";

const LINKS = [
  { label: "About", id: "about" },
  { label: "Services", id: "services" },
  { label: "Work", id: "work" },
  { label: "Contact", id: "contact" },
];

const scrollTo = (id) => scrollToId(id);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled
          ? "bg-[#050505]/70 backdrop-blur-xl border-b border-[#242424] py-3"
          : "bg-transparent border-b border-transparent py-5"
      }`}
      data-testid="navbar"
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-6 md:px-10 flex items-center justify-between">
        <button
          onClick={() => scrollTo("hero")}
          className="flex items-center gap-3 md:gap-4 group"
          data-testid="nav-logo"
        >
          <img src={ASSETS.schnauzerDog} alt="Silver Schnauzer" className="h-14 w-12 sm:h-16 sm:w-14 md:h-20 md:w-16 object-contain" />
          <span className="font-display text-xl md:text-2xl tracking-tight leading-none">
            Silver Schnauzer
            <span className="block font-mono-accent text-[10px] md:text-[11px] tracking-[0.35em] text-[#8b8b8b] mt-0.5 md:-mt-2.5">
              STUDIOS
            </span>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollTo(l.id)}
              className="relative font-mono-accent text-[11px] uppercase tracking-[0.22em] text-[#a9a9a9] hover:text-[#eaeaea] transition-colors duration-300 after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-gold after:transition-all after:duration-300 hover:after:w-full"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:block">
          <Magnetic>
            <button
              onClick={() => scrollTo("contact")}
              className="inline-flex items-center rounded-full border border-[#3a3a3a] px-5 py-2 font-mono-accent text-[11px] uppercase tracking-[0.2em] text-[#eaeaea] hover:bg-[#eaeaea] hover:text-[#050505] hover:border-[#eaeaea] transition-colors duration-300"
              data-testid="nav-cta"
            >
              Let's Talk
            </button>
          </Magnetic>
        </div>

        <button
          onClick={() => setOpen(true)}
          className="md:hidden -mr-2.5 grid place-items-center h-11 w-11 text-[#eaeaea] active:scale-90 transition-transform duration-200"
          data-testid="nav-menu-open"
          aria-label="Open menu"
        >
          <Menu size={24} />
        </button>
      </div>
    </motion.header>

    {createPortal(
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ backgroundColor: "#0a0a0c" }}
            className="fixed inset-0 z-[100] md:hidden flex flex-col"
            data-testid="mobile-menu"
          >
            {/* header row */}
            <div className="flex items-center justify-between px-6 h-[72px] border-b border-[#242424]">
              <span className="font-display text-[15px] tracking-tight leading-none">
                Silver Schnauzer
                <span className="block font-mono-accent text-[9px] tracking-[0.35em] text-[#8b8b8b] mt-1">
                  STUDIOS
                </span>
              </span>
              <button
                onClick={() => setOpen(false)}
                data-testid="nav-menu-close"
                aria-label="Close menu"
                className="grid place-items-center h-11 w-11 rounded-full border border-[#2a2a2a] text-[#eaeaea] active:scale-90 transition-transform duration-200"
              >
                <X size={20} />
              </button>
            </div>

            {/* nav items */}
            <nav className="flex flex-col px-6 pt-4">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.07 }}
                  onClick={() => {
                    scrollTo(l.id);
                    setOpen(false);
                  }}
                  className="group flex items-center justify-between py-6 border-b border-[#1c1c1c] text-left"
                  data-testid={`mobile-link-${l.id}`}
                >
                  <span className="flex items-baseline gap-4">
                    <span className="font-mono-accent text-[11px] text-gold">0{i + 1}</span>
                    <span className="font-display text-4xl tracking-tight text-[#eaeaea] group-hover:text-gold transition-colors duration-300">
                      {l.label}
                    </span>
                  </span>
                  <ArrowUpRight size={22} className="text-[#5a5a5a] group-hover:text-gold transition-colors duration-300" />
                </motion.button>
              ))}
            </nav>

            {/* footer */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-auto px-6 pb-10 pt-6"
            >
              <button
                onClick={() => {
                  scrollTo("contact");
                  setOpen(false);
                }}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#ff6b6b] text-[#0a0a0a] py-4 font-mono-accent text-[12px] uppercase tracking-[0.2em] font-medium"
                data-testid="mobile-cta"
              >
                Let's Talk
                <ArrowUpRight size={16} />
              </button>
              <a
                href="mailto:bigdogs@silverschnauzerstudios.com"
                className="block text-center font-mono-accent text-[11px] tracking-[0.15em] text-[#8b8b8b] mt-5"
              >
                bigdogs@silverschnauzerstudios.com
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>,
      document.body
    )}
    </>
  );
};
